/**
 * BB language + bible catalog fetcher with localStorage TTL cache.
 *
 * The reader's multi-bible picker needs two things:
 *   - List of languages that have at least one bible (2,500+ rows).
 *   - List of bibles in a given language (~16 rows typical, up to ~200
 *     for English).
 *
 * BB pages by 25 by default, but supports `limit=2000` for one-shot
 * fetches. We do one big request and cache for 24h — the catalog
 * doesn't churn, and a 200 KB JSON parse beats 100 round-trips.
 */

const API_BASE = 'https://4.dbt.io/api';
const TTL_MS = 24 * 60 * 60 * 1000;

export type Language = {
  id: number;
  iso: string;
  name: string;
  autonym: string | null;
  bibles: number;
};

export type Bible = {
  abbr: string;
  name: string;
  vname: string | null;
  language: string;
  iso: string;
  hasText: boolean;
  hasOT: boolean;
  hasNT: boolean;
};

function apiKey(): string | undefined {
  const key = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_BIBLE_BRAIN_KEY;
  return key && key.length > 0 ? key : undefined;
}

type Cached<T> = { value: T; cachedAt: number };

function cacheGet<T>(key: string): T | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Cached<T>;
    if (Date.now() - parsed.cachedAt > TTL_MS) return null;
    return parsed.value;
  } catch {
    return null;
  }
}

function cacheSet<T>(key: string, value: T): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify({ value, cachedAt: Date.now() } satisfies Cached<T>));
  } catch {
    /* quota — ignore */
  }
}

const LANG_KEY = '_lg1';
const BIBLES_PREFIX = '_bb1_';

export async function fetchLanguages(): Promise<Language[]> {
  const cached = cacheGet<Language[]>(LANG_KEY);
  if (cached) return cached;
  const key = apiKey();
  if (!key) return [];
  const list: Language[] = [];
  // BB v4 enforces a maximum limit of 250 on this endpoint; page through.
  let page = 1;
  while (true) {
    const url = `${API_BASE}/languages?v=4&limit=250&page=${page}&include_translations=true&has_bibles=true&key=${encodeURIComponent(key)}`;
    const data = await fetchJson(url);
    const items = (data?.data ?? []) as Array<{
      id?: number;
      iso?: string;
      name?: string;
      autonym?: string | null;
      bibles?: number;
    }>;
    for (const item of items) {
      if (!item.iso || !item.name) continue;
      list.push({
        id: item.id ?? 0,
        iso: item.iso,
        name: item.name,
        autonym: item.autonym ?? null,
        bibles: item.bibles ?? 0,
      });
    }
    const pagination = data?.meta?.pagination as
      | { last_page?: number; current_page?: number }
      | undefined;
    if (!pagination || (pagination.last_page ?? page) <= page) break;
    page += 1;
    if (page > 20) break; // hard safety net
  }
  // Deduplicate by ISO + sort alphabetically by name.
  const dedup = Array.from(new Map(list.map((l) => [l.iso, l])).values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
  cacheSet(LANG_KEY, dedup);
  return dedup;
}

export async function fetchBiblesForLanguage(languageIso: string): Promise<Bible[]> {
  const cacheKey = BIBLES_PREFIX + languageIso;
  const cached = cacheGet<Bible[]>(cacheKey);
  if (cached) return cached;
  const key = apiKey();
  if (!key) return [];
  const url = `${API_BASE}/bibles?v=4&language_code=${encodeURIComponent(languageIso)}&limit=200&key=${encodeURIComponent(key)}`;
  const data = await fetchJson(url);
  const items = (data?.data ?? []) as Array<{
    abbr?: string;
    name?: string;
    vname?: string | null;
    language?: string;
    iso?: string;
    filesets?: Record<string, Array<{ type?: string }>>;
  }>;
  const bibles: Bible[] = [];
  for (const item of items) {
    if (!item.abbr || !item.name || !item.iso) continue;
    const filesets = Object.values(item.filesets ?? {}).flat() as Array<{
      type?: string;
      size?: string;
    }>;
    const hasText = filesets.some((f) => f?.type?.startsWith('text_'));
    // BB size codes: 'C' = complete, 'N' = NT only, 'O' = OT only, 'P' = partial.
    // If size is absent on every fileset, assume complete (backwards compat).
    const anySized = filesets.some((f) => f.size);
    const hasOT = !anySized || filesets.some((f) => f.size === 'C' || f.size === 'O');
    const hasNT = !anySized || filesets.some((f) => f.size === 'C' || f.size === 'N');
    bibles.push({
      abbr: item.abbr,
      name: item.name,
      vname: item.vname ?? null,
      language: item.language ?? '',
      iso: item.iso,
      hasText,
      hasOT,
      hasNT,
    });
  }
  // Sort: text-capable first, then alpha by display name.
  bibles.sort((a, b) => {
    if (a.hasText !== b.hasText) return a.hasText ? -1 : 1;
    return (a.vname ?? a.name).localeCompare(b.vname ?? b.name);
  });
  cacheSet(cacheKey, bibles);
  return bibles;
}

async function fetchJson(url: string): Promise<{ data?: unknown[]; meta?: Record<string, unknown> } | null> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) return null;
  return res.json() as Promise<{ data?: unknown[]; meta?: Record<string, unknown> }>;
}
